import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js';
import { Table, TabSwitcher } from 'equal-ds-ui';
import type { TableColumn } from 'equal-ds-ui';
import type { AAConversionOverallResponse, AAConversionEntry } from '../../../types/smart-router';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface Props {
  data: AAConversionOverallResponse;
  level: 'consent' | 'user';
  onLevelChange: (level: 'consent' | 'user') => void;
}

const AA_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export const AAConversionSection: React.FC<Props> = ({ data, level, onLevelChange }) => {
  const conversions = data?.conversions ?? [];

  const columns = useMemo<TableColumn<AAConversionEntry>[]>(() => [
    { key: 'aa_id', header: 'AA', width: 150, accessor: (row) => row.aa_id },
    { key: 'total', header: 'Traffic', width: 120, accessor: (row) => row.total.toLocaleString() },
    { key: 'success', header: 'Success', width: 120, accessor: (row) => row.success.toLocaleString() },
    {
      key: 'success_rate', header: 'Conv. Rate', width: 120,
      render: (_val, row) => (
        <span className={`font-semibold ${row.success_rate >= 70 ? 'text-green-600' : row.success_rate >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
          {row.success_rate.toFixed(1)}%
        </span>
      ),
    },
  ], []);

  const barData = {
    labels: conversions.map(r => r.aa_id),
    datasets: [{
      label: 'Conversion Rate',
      data: conversions.map(r => r.success_rate),
      backgroundColor: AA_COLORS.slice(0, conversions.length),
      borderRadius: 6,
      barThickness: 32,
    }],
  };

  const barOptions = {
    responsive: true, maintainAspectRatio: false, indexAxis: 'y' as const,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx: any) => `${ctx.parsed.x.toFixed(1)}%` } } },
    scales: { x: { beginAtZero: true, ticks: { callback: (v: any) => `${v}%` } }, y: { grid: { display: false } } },
  };

  return (
    <div className="border border-border-default rounded-xl shadow-md overflow-hidden">
      <div className="bg-gradient-to-b from-primary-50 to-primary-100 px-4 py-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary tracking-wider">AA Conversion Rates</h2>
        <TabSwitcher
          tabs={[{ id: 'consent', label: 'Consent Level' }, { id: 'user', label: 'User Level' }]}
          activeTab={level}
          onTabChange={(tab) => onLevelChange(tab as 'consent' | 'user')}
          variant="horizontal"
        />
      </div>
      <div className="bg-white p-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Table<AAConversionEntry> columns={columns} data={conversions} />
          <div className="h-48"><Bar data={barData} options={barOptions} /></div>
        </div>
      </div>
    </div>
  );
};
